import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, fetchProducts, smartMatch } from "../lib/api";
import ProductCard from "../components/ProductCard";
import ProductCardRow from "../components/ProductCardRow";
import ArticleGroupCard from "../components/ArticleGroupCard";
import FinderCartBar from "../components/FinderCartBar";
import { Filter, Loader2, AlertTriangle, Sparkles } from "lucide-react";

const CATEGORIES = ["Pipe Clamp Sets", "DIN 3567 Heavy Clamps", "Half Pipe Clamps", "Cap Clamps", "Other"];
const MATERIALS = ["304", "316", "galvanized"];
const LOAD_CLASSES = ["light", "medium", "heavy"];
const APPLICATIONS = ["HVAC", "Food", "Chemical", "Marine", "Offshore", "Pharma", "Hydrogen", "Data_center"];
const KIT_TYPES = [
  { v: "complete", labelKey: "kit_types.complete" },
  { v: "set", labelKey: "kit_types.set" },
  { v: "set+stud", labelKey: "kit_types.set_stud" },
  { v: "set+thread", labelKey: "kit_types.set_thread" },
  { v: "din3567-set", labelKey: "kit_types.din3567_set" },
  { v: "din3567-half", labelKey: "kit_types.din3567_half" },
  { v: "half", labelKey: "kit_types.half" },
  { v: "cap", labelKey: "kit_types.cap" },
];

export default function ProductsPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [viewMode, setViewMode] = useState("groups"); // groups | flat
  const [loading, setLoading] = useState(true);
  const [warnings, setWarnings] = useState([]);
  const [recommendations, setRecommendations] = useState([]);

  const filters = useMemo(() => ({
    category: searchParams.get("category") || "",
    material: searchParams.get("material") || "",
    dn: searchParams.get("dn") || "",
    inch: searchParams.get("inch") || "",
    d1_mm: searchParams.get("d1_mm") || "",
    application: searchParams.get("application") || "",
    load_class: searchParams.get("load_class") || "",
    kit_type: searchParams.get("kit_type") || "",
    search: searchParams.get("search") || "",
  }), [searchParams]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const hasTechnical = filters.dn || filters.d1_mm || filters.inch || filters.application;
        if (hasTechnical) {
          // Technical drill-down → flat smart-match results (one row per family)
          setViewMode("flat");
          const payload = {
            d1_mm: filters.d1_mm ? parseFloat(filters.d1_mm) : undefined,
            dn: filters.dn ? parseInt(filters.dn) : undefined,
            inch: filters.inch || undefined,
            material: filters.material || undefined,
            application: filters.application || undefined,
            load_class: filters.load_class || undefined,
          };
          const res = await smartMatch(payload);
          let matches = res.matches;
          if (filters.category) matches = matches.filter((p) => p.category === filters.category);
          if (filters.kit_type) {
            if (filters.kit_type === "complete") {
              matches = matches.filter((p) => ["set", "set+stud", "set+thread", "din3567", "din3567-set"].includes(p.kit_type));
            } else {
              matches = matches.filter((p) => p.kit_type === filters.kit_type);
            }
          }
          if (filters.search) {
            const s = filters.search.toLowerCase();
            matches = matches.filter((p) =>
              p.name.toLowerCase().includes(s) || p.sku.toLowerCase().includes(s)
            );
          }
          setProducts(matches);
          setGroups([]);
          setWarnings(res.warnings || []);
          setRecommendations(res.recommendations || []);
        } else if (filters.search) {
          // Free-text search → flat results (one card per matching family)
          setViewMode("flat");
          const params = {};
          if (filters.category) params.category = filters.category;
          if (filters.material) params.material = filters.material;
          if (filters.load_class) params.load_class = filters.load_class;
          if (filters.kit_type) params.kit_type = filters.kit_type;
          params.search = filters.search;
          const data = await fetchProducts(params);
          setProducts(data);
          setGroups([]);
          setWarnings([]);
          setRecommendations([]);
        } else {
          // Default browsing → grouped catalog
          setViewMode("groups");
          const res = await api.get("/article-groups");
          let gs = res.data.groups || [];
          if (filters.category) {
            gs = gs.filter((g) => g.category === filters.category);
          }
          if (filters.material) {
            const m = filters.material.toLowerCase();
            gs = gs.filter((g) => (g.materials_available || []).includes(m));
          }
          if (filters.kit_type) {
            const kt = filters.kit_type;
            const completeSet = new Set(["set", "set+stud", "set+thread", "din3567", "din3567-set"]);
            gs = gs.filter((g) =>
              (g.kit_types_available || []).some((c) =>
                kt === "complete" ? completeSet.has(c.kit_type) : c.kit_type === kt
              )
            );
          }
          setGroups(gs);
          setProducts([]);
          setWarnings([]);
          setRecommendations([]);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [filters]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (!value) next.delete(key);
    else next.set(key, value);
    setSearchParams(next);
  };
  const clearAll = () => setSearchParams(new URLSearchParams());
  const activeCount = Object.values(filters).filter(Boolean).length;

  return (
    <div className="min-h-screen bg-white" data-testid="products-page">
      {/* Section header in NIBRO style */}
      <div className="bg-nibro-sage-pale py-12">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
          <div className="nibro-frame">
            <h1 className="nibro-section-title">{t("products_page.title").toUpperCase()}</h1>
            <p className="mt-3 text-center text-sm text-zinc-600">
              {filters.category || t("products_page.subtitle_default", { defaultValue: "Alle pijpbeugels, pijpschoenen, U-bolts en heavy-duty supports" })}
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-5 py-10 lg:px-10">
        <div className="mb-6">
          <input
            value={filters.search}
            onChange={(e) => setFilter("search", e.target.value)}
            placeholder={t("products_page.search_placeholder", { defaultValue: "Zoek op naam, SKU of omschrijving..." })}
            className="w-full max-w-xl rounded-md border border-zinc-200 bg-white px-4 py-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-nibro-sage focus:outline-none focus:ring-2 focus:ring-nibro-sage/30"
            data-testid="products-search-input"
          />
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[280px_1fr]">
          {/* Filters */}
          <aside className="space-y-6" data-testid="products-filters">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Filter size={14} className="text-nibro-sage-dark" />
                <span className="label-uppercase">{t("products_page.filter_label")}</span>
                {activeCount > 0 && (
                  <span className="rounded-full bg-aramfix-red px-2 py-0.5 text-[10px] font-bold text-white">
                    {activeCount}
                  </span>
                )}
              </div>
              {activeCount > 0 && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="text-xs font-semibold text-nibro-sage-dark hover:text-nibro-sage-darker"
                  data-testid="filter-clear-all"
                >
                  {t("products_page.clear_filters")}
                </button>
              )}
            </div>

            <FilterGroup label={t("products_page.filter_type")}>
              {KIT_TYPES.map((k) => (
                <FilterPill
                  key={k.v}
                  active={filters.kit_type === k.v}
                  onClick={() => setFilter("kit_type", filters.kit_type === k.v ? "" : k.v)}
                  testId={`filter-kit-${k.v.replace(/\+/g, "-")}`}
                >
                  {t(k.labelKey)}
                </FilterPill>
              ))}
            </FilterGroup>

            <FilterGroup label={t("products_page.filter_category")}>
              {CATEGORIES.map((c) => (
                <FilterPill
                  key={c}
                  active={filters.category === c}
                  onClick={() => setFilter("category", filters.category === c ? "" : c)}
                  testId={`filter-cat-${c.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {c}
                </FilterPill>
              ))}
            </FilterGroup>

            <FilterGroup label={t("products_page.filter_material")}>
              {MATERIALS.map((m) => (
                <FilterPill
                  key={m}
                  active={filters.material === m}
                  onClick={() => setFilter("material", filters.material === m ? "" : m)}
                  testId={`filter-mat-${m}`}
                >
                  {m.toUpperCase()}
                </FilterPill>
              ))}
            </FilterGroup>

            <FilterGroup label={t("products_page.filter_load_class")}>
              {LOAD_CLASSES.map((l) => (
                <FilterPill
                  key={l}
                  active={filters.load_class === l}
                  onClick={() => setFilter("load_class", filters.load_class === l ? "" : l)}
                  testId={`filter-load-${l}`}
                >
                  {l}
                </FilterPill>
              ))}
            </FilterGroup>

            <FilterGroup label={t("products_page.filter_application")}>
              {APPLICATIONS.map((a) => (
                <FilterPill
                  key={a}
                  active={filters.application === a.toLowerCase()}
                  onClick={() => setFilter("application", filters.application === a.toLowerCase() ? "" : a.toLowerCase())}
                  testId={`filter-app-${a.toLowerCase()}`}
                >
                  {a.replace(/_/g, " ")}
                </FilterPill>
              ))}
            </FilterGroup>

            <FilterGroup label={t("products_page.filter_size", { defaultValue: "Maat" })}>
              <div className="w-full space-y-2">
                <input
                  value={filters.dn}
                  onChange={(e) => setFilter("dn", e.target.value)}
                  placeholder="DN (bijv. 25)"
                  className="w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm focus:border-nibro-sage focus:outline-none"
                  data-testid="filter-dn-input"
                />
                <input
                  value={filters.d1_mm}
                  onChange={(e) => setFilter("d1_mm", e.target.value)}
                  placeholder="D1 mm (bijv. 33.7)"
                  className="w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm focus:border-nibro-sage focus:outline-none"
                  data-testid="filter-d1-input"
                />
                <input
                  value={filters.inch}
                  onChange={(e) => setFilter("inch", e.target.value)}
                  placeholder='Inch (bijv. 1")'
                  className="w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm focus:border-nibro-sage focus:outline-none"
                  data-testid="filter-inch-input"
                />
              </div>
            </FilterGroup>
          </aside>

          {/* Results */}
          <div>
            {(warnings.length > 0 || recommendations.length > 0) && (
              <div className="mb-6 space-y-2" data-testid="smart-match-notices">
                {warnings.map((w, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                  >
                    <AlertTriangle size={16} className="mt-0.5 flex-shrink-0 text-amber-600" />
                    <div>
                      <span className="font-mono text-[10px] font-bold uppercase tracking-wider">{t("products_page.warning", { defaultValue: "Waarschuwing" })}</span>
                      <p className="mt-0.5">{w}</p>
                    </div>
                  </div>
                ))}
                {recommendations.map((r, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-3 rounded-md border border-nibro-sage/50 bg-nibro-sage-pale px-4 py-3 text-sm text-zinc-800"
                  >
                    <Sparkles size={16} className="mt-0.5 flex-shrink-0 text-nibro-sage-dark" />
                    <div>
                      <span className="font-mono text-[10px] font-bold uppercase tracking-wider text-nibro-sage-dark">{t("products_page.advice", { defaultValue: "Advies" })}</span>
                      <p className="mt-0.5">{r}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mb-4 flex items-center justify-between">
              <div className="text-sm text-zinc-500">
                {loading
                  ? t("products_page.loading")
                  : viewMode === "groups"
                    ? t("products_page.groups_count", { defaultValue: `${groups.length} artikelgroepen`, n: groups.length })
                    : t("products_page.results_count", { n: products.length })}
              </div>
            </div>

            {loading ? (
              <div className="flex h-64 items-center justify-center">
                <Loader2 className="animate-spin text-nibro-sage-dark" />
              </div>
            ) : viewMode === "groups" ? (
              groups.length === 0 ? (
                <div className="rounded-md border border-zinc-200 bg-white p-12 text-center">
                  <div className="font-display text-xl text-zinc-900">{t("products_page.no_groups", { defaultValue: "Geen artikelgroepen gevonden" })}</div>
                  <button
                    type="button"
                    onClick={clearAll}
                    className="mt-4 text-sm font-semibold text-nibro-sage-dark hover:underline"
                  >
                    {t("products_page.clear_filters")}
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="article-groups-grid">
                  {groups.map((g) => (
                    <ArticleGroupCard key={g.article_group_key} group={g} />
                  ))}
                </div>
              )
            ) : products.length === 0 ? (
              <div className="rounded-md border border-zinc-200 bg-white p-12 text-center">
                <div className="font-display text-xl text-zinc-900">{t("products_page.no_results")}</div>
                <button
                  type="button"
                  onClick={clearAll}
                  className="mt-4 text-sm font-semibold text-nibro-sage-dark hover:underline"
                >
                  {t("products_page.clear_filters")}
                </button>
              </div>
            ) : (
              (() => {
                // When the user came in via the Pipe Clamp Finder with a
                // technical filter (DN / mm / inch), render a vertical
                // single-column list — easier to scan which clamp families
                // carry the requested size — and pass the dn context to
                // ProductCard so clicking lands on the detail page with
                // that variant already selected.
                const isTechnical = !!(filters.dn || filters.d1_mm || filters.inch);
                if (isTechnical) {
                  return (
                    <div className="flex flex-col gap-2" data-testid="finder-results-list">
                      {products.map((p) => (
                        <ProductCardRow
                          key={p.id}
                          p={p}
                          dnContext={filters.dn || undefined}
                          d1MmContext={filters.d1_mm || undefined}
                          inchContext={filters.inch || undefined}
                        />
                      ))}
                    </div>
                  );
                }
                return (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {products.map((p) => (
                      <ProductCard key={p.id} p={p} />
                    ))}
                  </div>
                );
              })()
            )}
          </div>
        </div>
      </div>
      {/* Floating cart bar — only on finder-results pages (technical
          filter active). Provides instant feedback after each quick-add
          plus a one-click route to the cart. */}
      {(filters.dn || filters.d1_mm || filters.inch) && <FinderCartBar />}
    </div>
  );
}

function FilterGroup({ label, children }) {
  return (
    <div className="space-y-3">
      <div className="label-uppercase">{label}</div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}
function FilterPill({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
        active
          ? "border-nibro-sage bg-nibro-sage text-white"
          : "border-zinc-200 bg-white text-zinc-600 hover:border-nibro-sage hover:text-zinc-900"
      }`}
    >
      {children}
    </button>
  );
}
